import { useState } from "react";

export type ProjectType = "design" | "custom" | "knockdown" | "rear-block" | "development" | "unsure";
export type ContactMethod = "email" | "phone";
export type FormStep = 1 | 2 | "done";

export type ConsultationValues = {
  projectType: ProjectType | "";
  landStatus: string;
  suburb: string;
  suburbUndecided: boolean;
  timing: string;
  name: string;
  contactMethod: ContactMethod;
  email: string;
  phone: string;
  message: string;
  budget: string;
  marketingConsent: boolean;
  design: string;
  request: string;
};

export type ConsultationErrors = Partial<Record<keyof ConsultationValues, string>>;

const defaults: ConsultationValues = {
  projectType: "",
  landStatus: "",
  suburb: "",
  suburbUndecided: false,
  timing: "",
  name: "",
  contactMethod: "email",
  email: "",
  phone: "",
  message: "",
  budget: "",
  marketingConsent: false,
  design: "",
  request: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useConsultationForm(initial: Partial<ConsultationValues>) {
  const [step, setStep] = useState<FormStep>(1);
  const [values, setValues] = useState<ConsultationValues>({ ...defaults, ...initial });
  const [errors, setErrors] = useState<ConsultationErrors>({});

  const update = <K extends keyof ConsultationValues>(key: K, value: ConsultationValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validateStepOne = (): ConsultationErrors => {
    const result: ConsultationErrors = {};
    if (!values.projectType) result.projectType = "Choose the type of project you’re considering.";
    if (!values.landStatus) result.landStatus = "Tell us where you’re at with land.";
    if (!values.suburbUndecided && !values.suburb.trim()) {
      result.suburb = "Enter a suburb or postcode, or choose “Still deciding”.";
    }
    return result;
  };

  const validateStepTwo = (): ConsultationErrors => {
    const result: ConsultationErrors = {};
    if (!values.name.trim()) result.name = "Enter your name.";
    if (values.contactMethod === "email" && !EMAIL_PATTERN.test(values.email.trim())) {
      result.email = "Enter a valid email address, for example name@example.com.";
    }
    if (values.contactMethod === "phone" && values.phone.replace(/\D/g, "").length < 8) {
      result.phone = "Enter a phone number with at least 8 digits.";
    }
    return result;
  };

  const next = (): boolean => {
    const result = validateStepOne();
    setErrors(result);
    if (Object.keys(result).length === 0) {
      setStep(2);
      return true;
    }
    return false;
  };

  const back = () => {
    setErrors({});
    setStep(1);
  };

  const submit = (): boolean => {
    const result = validateStepTwo();
    setErrors(result);
    if (Object.keys(result).length === 0) {
      setStep("done");
      return true;
    }
    return false;
  };

  const restart = () => {
    setValues({ ...defaults, ...initial });
    setErrors({});
    setStep(1);
  };

  return { step, values, errors, update, next, back, submit, restart };
}
